import { ApiError, get, getAuthToken, post, setAuthToken } from "#/client";
import Tts from "#/feature/tts-playback/tts-plugin";
import { Capacitor } from "@capacitor/core";
import { signal } from "alien-signals";

export interface User {
  id: number;
  username: string;
  role: "admin" | "user";
  totp_enabled: boolean;
}

export const userSignal = signal<User | null>(null);
export const authLoadingSignal = signal(true);
export const connectionErrorSignal = signal(false);

function isUnauthenticatedError(error: unknown): boolean {
  return (
    error instanceof ApiError && (error.response.status === 401 || error.response.status === 403)
  );
}

export async function checkAuth(): Promise<void> {
  authLoadingSignal(true);
  // Keep connectionError until we know the outcome — clearing it here lets AuthGate
  // briefly treat the session as logged-out and flash /login.

  const token = getAuthToken();
  if (!token) {
    userSignal(null);
    connectionErrorSignal(false);
    authLoadingSignal(false);
    return;
  }

  try {
    const me = await get<User>("/auth/me");
    userSignal(me);
    connectionErrorSignal(false);
  } catch (error) {
    if (isUnauthenticatedError(error)) {
      setAuthToken(null);
      if (Capacitor.getPlatform() !== "web") {
        void Tts.setToken({ token: null });
      }
      userSignal(null);
      connectionErrorSignal(false);
    } else {
      // Network failure or non-auth HTTP error — don't treat as logged out.
      userSignal(null);
      connectionErrorSignal(true);
    }
  } finally {
    authLoadingSignal(false);
  }
}

/** Persist session locally. Caller navigates after success. */
export function login(token: string, user: User): void {
  setAuthToken(token);
  if (Capacitor.getPlatform() !== "web") {
    void Tts.setToken({ token });
  }
  userSignal(user);
  connectionErrorSignal(false);
}

/** Clear session. Caller navigates to `/login` after. */
export async function logout(): Promise<void> {
  try {
    await post("/auth/logout");
  } catch (error) {
    console.error("Logout failed", error);
  }
  setAuthToken(null);
  if (Capacitor.getPlatform() !== "web") {
    void Tts.setToken({ token: null });
  }
  userSignal(null);
  connectionErrorSignal(false);
}
